/* eslint-disable no-console */

import { getDataLayer, Property, pushToDataLayer } from './tracking';
import { devLog } from './devLog';

// Queue for GA4 events waiting for consent
let pendingGA4Events: Array<{ event: string; properties: Property }> = [];
let consentReady = false;
let isPolling = false; // Add flag to prevent duplicate polling

/**
 * Get current pending GA4 events (read-only access).
 */
export const getPendingGA4Events = (): ReadonlyArray<{
  event: string;
  properties: Property;
}> => pendingGA4Events;

/**
 * Check if consent is ready (read-only access).
 */
export const isConsentReady = (): boolean => consentReady;

/**
 * Add event to queue (used by trackGTM).
 */
export const queueGA4Event = (event: string, properties: Property): void => {
  pendingGA4Events.push({ event, properties });
  devLog(
    `[GTM Consent] Queued event: ${event} (queue length: ${pendingGA4Events.length})`,
  );
};

/**
 * Reset queue (for testing).
 */
export const resetGA4Queue = (): void => {
  pendingGA4Events = [];
  consentReady = false;
  isPolling = false;
};

/**
 * Check if GTM consent update exists in dataLayer with analytics consent granted.
 * Checks both root-level (Tag Assistant) and nested (production) structures.
 */
export const hasGTMConsentUpdate = (): boolean => {
  const dl = getDataLayer();

  for (let i = dl.length - 1; i >= 0; i--) {
    const entry = dl[i] as Record<string, unknown>;

    // Check root-level structure
    if (
      entry?.event === 'gtm_consent_update' &&
      entry?.analytics_storage === 'granted'
    ) {
      return true;
    }

    // Check nested structure
    const value = entry?.value as Record<string, unknown> | undefined;
    if (
      value?.event === 'gtm_consent_update' &&
      value?.analytics_storage === 'granted'
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Check OptanonConsent cookie for returning visitors.
 * OneTrust stores consent in format: "groups=1:1,2:1,3:1,4:1"
 * where X:1 means group X is granted, X:0 means denied.
 * Group 2 = analytics consent (required for GA4).
 */
const hasConsentCookie = (): boolean => {
  if (typeof document === 'undefined') return false;

  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('OptanonConsent='));

  if (!cookie) return false;

  const cookieValue = decodeURIComponent(cookie.split('=').slice(1).join('='));
  const groupsRegex = /groups=([^&]+)/;
  const groupsMatch = groupsRegex.exec(cookieValue);

  if (!groupsMatch) return false;

  // Check if group 2 is granted (:1) not denied (:0)
  const group2Regex = /(?:^|,)2:1(?:,|$)/;
  const hasCookie = group2Regex.test(groupsMatch[1]);
  devLog('[GTM Consent] Consent cookie check:', hasCookie);
  return hasCookie;
};

const flushGA4EventQueue = () => {
  if (pendingGA4Events.length === 0) return; // Guard against empty queue

  console.log(
    `[GTM Consent] Flushing ${pendingGA4Events.length} queued GA4 events`,
  );

  const eventsToFlush = [...pendingGA4Events];
  pendingGA4Events = [];
  // Mark consent as ready BEFORE pushing events to prevent race
  consentReady = true;

  eventsToFlush.forEach(({ event, properties }) => {
    pushToDataLayer({ event, ...properties });
  });
};

/**
 * Check if GTM container has loaded in dataLayer.
 */
const isGTMLoaded = (): boolean => {
  const dl = getDataLayer();
  if (!Array.isArray(dl)) return false;

  return dl.some((entry) => {
    const record = entry as Record<string, unknown>;
    return (
      record?.event === 'gtm.js' ||
      record?.event === 'gtm.start' ||
      'gtm.start' in record
    );
  });
};

/**
 * Wait for GTM consent update before firing GA4 ecommerce events.
 *
 * GTM Consent Mode fires 'gtm_consent_update' event after OneTrust consent is processed.
 * This event contains analytics_storage status which determines if GA4 can fire.
 *
 * OneTrust groups: 1=Necessary, 2=Analytics (required for GA4), 3=Functional, 4=Advertising
 * Example: ",1,3,2,4," means all groups accepted (group 2 = analytics consent granted).
 *
 * Waits 40s for user consent by default. If user abandons/denies, queue remains unflushed.
 * This prevents tracking users who didn't consent (correct GDPR behavior).
 */
export function waitForOneTrustReady({
  maxWaitMs = 40000, // 40 seconds - balances UX and resource usage
}: { maxWaitMs?: number } = {}): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();

  // Prevent duplicate polling instances
  if (isPolling) {
    devLog('[GTM Consent] Polling already in progress');
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    // Set polling flag IMMEDIATELY to prevent race conditions
    isPolling = true;

    try {
      // If consent already exists, resolve immediately
      if (hasGTMConsentUpdate()) {
        devLog('[GTM Consent] Consent already present in dataLayer');
        consentReady = true;
        isPolling = false; // Clear flag
        return resolve();
      }

      // Check if user has consent cookie (returning visitor)
      const hasCookie = hasConsentCookie();

      const startTime = Date.now();
      let resolved = false;

      // Poll dataLayer for consent instead of intercepting push
      // This is less invasive and preserves GTM/Tag Assistant functionality
      const pollForConsent = () => {
        if (resolved) return;

        try {
          // Check for consent event (authoritative signal)
          if (hasGTMConsentUpdate()) {
            resolved = true;
            isPolling = false; // Clear flag FIRST
            consentReady = true; // Set flag BEFORE flush
            devLog('[GTM Consent] Consent detected via polling');
            flushGA4EventQueue();
            return resolve();
          }
        } catch (err) {
          // If checking consent fails, stop polling
          resolved = true;
          isPolling = false;
          devLog('[GTM Consent] Error checking consent:', err);
          return resolve();
        }

        // Check for user-specified timeout (optional, 0 = wait indefinitely)
        const elapsed = Date.now() - startTime;
        // Check timeout (prevents indefinite polling)
        if (maxWaitMs > 0 && elapsed >= maxWaitMs) {
          isPolling = false; // Clear flag
          devLog('[GTM Consent] Max wait time reached, stopping polling');
          return resolve(); // Must resolve to complete the promise
        }

        // Cookie fallback for returning visitors
        // Wait 3s to give GTM consent event priority (reduces race conditions)
        // Only use cookie if GTM is loaded but consent event hasn't fired yet
        if (hasCookie && elapsed >= 3000 && isGTMLoaded()) {
          resolved = true;
          isPolling = false; // Clear flag FIRST
          consentReady = true; // Set flag BEFORE flush
          devLog(
            '[GTM Consent] Consent detected via cookie fallback (3s grace period)',
          );
          flushGA4EventQueue();
          return resolve();
        }

        // Continue polling every 100ms
        setTimeout(pollForConsent, 100);
      };

      // Wait for GTM to initialize before polling
      const startPolling = () => {
        if (resolved) return;

        try {
          const dl = window.dataLayer;
          if (!dl || !Array.isArray(dl)) {
            // Check timeout even during GTM initialization wait
            const elapsed = Date.now() - startTime;
            if (maxWaitMs > 0 && elapsed >= maxWaitMs) {
              isPolling = false;
              devLog('[GTM Consent] Max wait time reached before GTM loaded');
              return resolve();
            }
            setTimeout(startPolling, 100);
            return;
          }

          if (!isGTMLoaded()) {
            // Check timeout even during GTM load wait
            const elapsed = Date.now() - startTime;
            if (maxWaitMs > 0 && elapsed >= maxWaitMs) {
              isPolling = false;
              devLog('[GTM Consent] Max wait time reached before GTM loaded');
              return resolve();
            }
            setTimeout(startPolling, 100);
            return;
          }

          pollForConsent();
        } catch (err) {
          // If accessing dataLayer fails, stop polling
          resolved = true;
          isPolling = false;
          devLog('[GTM Consent] Error accessing dataLayer:', err);
          return resolve();
        }
      };

      startPolling();
    } catch (e) {
      devLog('[GTM Consent] Error during consent polling:', e);
      isPolling = false; // Clear flag on error
      resolve();
    }
  });
}
